import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from './base.service';
import { Observable, of, forkJoin } from 'rxjs';
import { ServicesHelper } from '../helpers/index';
import { Client, BlackList, ClientBasic } from '../models/index';
import { SelectorEntity } from '../models/selector-entity';
import { BusinessEntityType } from '../enums';

@Injectable()
export class ClientService extends BaseService<Client> {
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string) {
    const url: string = apiUrl + 'client/';
    super(apiUrl, url, httpClient);
  }

  public getBasicList(): Observable<Array<Client>> {
    const url: string = this.url + 'basic-list';
    const response: Observable<Array<Client>> = this.httpClient.get<Array<Client>>(url, {
      responseType: 'json',
      headers: ServicesHelper.getHeaders(),
    });
    return response;
  }

  public getSelectableEntities(): Observable<Array<SelectorEntity>> {
    const url: string = this.url + 'selectable';
    const response: Observable<Array<SelectorEntity>> = this.httpClient.get<Array<SelectorEntity>>(url, {
      responseType: 'json',
      headers: ServicesHelper.getHeaders(),
    });
    return response;
  }

  public checkBlacklist(client: Client): Observable<BlackList> {
    const url: string = this.url + 'blacklist';
    const response: Observable<BlackList> = this.httpClient.post<BlackList>(url, client, {
      responseType: 'json',
      headers: ServicesHelper.getHeaders(),
    });
    return response;
  }
  public clientIsLock(clientId: number, isLock: boolean): Observable<any> {
    const url: string = this.url + 'is-lock/' + clientId + '/' + isLock;
    const response: Observable<any> = this.httpClient.get<any>(url, {
      responseType: 'json',
      headers: ServicesHelper.getHeaders(),
    });
    return response;
  }

  public getReport(clientId: number): Observable<Blob> {
    const url: string = this.url + 'report/' + clientId;
    const response = this.httpClient.get(url, { responseType: 'blob', headers: ServicesHelper.getHeaders() });
    return response;
  }

  saveClients(clients: Array<Client>): Observable<Array<Client>> {
    if (clients.length === 0) {
      return of(new Array<Client>());
    }

    const clientsHttpRequests: Array<Observable<Client>> = new Array<Observable<Client>>();
    for (let i = 0; i < clients.length; i++) {
      clientsHttpRequests.push(this.post(clients[i]));
    }

    const response: Observable<Array<Client>> = forkJoin(clientsHttpRequests);
    return response;
  }

  clientExist(name: string, rfc: string, clientId: number | null = null): Observable<boolean> {
    let url: string = this.url + 'clientExist/' + name + '/' + rfc;
    if (clientId) url = `${url}?clientId=${clientId}`;
    const response: Observable<boolean> = this.httpClient.get<boolean>(url, { responseType: 'json', headers: ServicesHelper.getHeaders() });

    return response;
  }

  clientExistByBEntityType(entityType: BusinessEntityType, clientBasic: ClientBasic): Observable<Client> {
    const response = this.httpClient.put<Client>(`${this.url}clientExist?entityType=${(entityType as number)}`,
      clientBasic,
      {
         responseType: 'json', 
         headers: ServicesHelper.getHeaders(),  
      }
    );

    return response;
  }

  getClientExist(name: string, rfc: string, clientId: number | null = null): Observable<Client> {
    let url: string = this.url + 'getClientExist/' + name + '/' + rfc;
    if (clientId) url = `${url}?clientId=${clientId}`;
    const response: Observable<Client> = this.httpClient.get<Client>(url, { responseType: 'json', headers: ServicesHelper.getHeaders() });

    return response;
  }

  checkClientNumberExist(clientNumber: string): Observable<boolean> {
    let url: string = this.url + 'checkClientNumberExist/' + clientNumber;
    const response: Observable<boolean> = this.httpClient.get<boolean>(url, { responseType: 'json', headers: ServicesHelper.getHeaders() });

    return response;
  }

  getClientByNumber(clientNumber: string): Observable<Client> {
    let url: string = this.url + 'getClientByNumber/' + clientNumber;
    const response: Observable<Client> = this.httpClient.get<Client>(url, { responseType: 'json', headers: ServicesHelper.getHeaders() });

    return response;
  }
}
